import { useAuthentication } from 'v2/hooks/useAuthentication';
import { fromReduxUserDuckToUser } from './mapping';
import {
  CURRENT_USER_SHOW_SUCCESS,
  CURRENT_USER_SHOW_ERROR,
  CLEAR_CURRENT_USER,
  FETCH_CURRENT_USER_MEMBERSHIP_SUCCESS,
} from '../../../ducks/userActionTypes';
import type { AuthStatus, User } from './types';

interface ReduxAction {
  type: string;
}

interface ReduxStoreApi {
  getState: () => { user?: { currentUser?: unknown; currentUserMembership?: unknown } };
  dispatch: (action: unknown) => unknown;
}

function statusFor(user: User | null): AuthStatus {
  return user ? 'authenticated' : 'anonymous';
}

function syncFromRedux(store: ReduxStoreApi) {
  const { currentUser, currentUserMembership } = store.getState().user ?? {};
  const user = fromReduxUserDuckToUser(currentUser, currentUserMembership);

  useAuthentication.setState({ user, status: statusFor(user) });
}

/**
 * Redux middleware (wired up in src/store.js) that bridges the legacy Redux
 * user state into the v2 Zustand auth store, so v2 code only ever calls
 * useAuthentication() and never touches Redux directly.
 *
 * Why this has to be middleware, not a subscribe()/dispatch wrapper:
 * fetchCurrentUser's CURRENT_USER_SHOW_SUCCESS/ERROR actions are dispatched
 * through Redux's internal middleware chain. Anything sitting outside that
 * chain never sees them fire — middleware sits inside it, so it does.
 *
 * Why `createLogoutAction` is passed in as a parameter instead of importing
 * `ducks/auth.duck` directly: that file transitively imports the whole app,
 * including `import.meta.glob` (Vite-only), which crashes under bun:test.
 * src/store.js already imports the whole reducer tree anyway, so it's the
 * one place that can safely hand the real logout thunk in here.
 *
 * SSR safety:
 *
 *   Client (one browser tab)        Server (one shared Node process)
 *   ──────────────────────          ────────────────────────────────
 *   1 user  →  1 auth store         many users  →  1 auth store (!)
 *   safe to write directly          writing here would leak one
 *                                   request's user into another's response
 *
 * Every write in this file is guarded by `typeof window`, so it's a no-op
 * on the server — the store is only ever written client-side, once per tab,
 * where there's no cross-request sharing to worry about.
 */
export const createAuthBridgeMiddleware =
  (createLogoutAction: () => unknown) => (store: ReduxStoreApi) => {
    // Wires up the real logout (replacing the auth store's no-op placeholder) so
    // v2 code can call useAuthentication(s => s.logout)() without ever
    // importing Redux. Runs once per store setup, not per action — same SSR
    // guard as above, since the legacy logout thunk touches window/document
    // directly, and we don't want a stale request-scoped reference left
    // behind in the shared singleton.
    if (typeof window !== 'undefined') {
      useAuthentication.setState({ logout: () => store.dispatch(createLogoutAction()) });

      // After SSR the store is created from preloaded state that already holds
      // the user. fetchCurrentUser skips its re-fetch when that user is under a
      // second old, so on a fast hydration CURRENT_USER_SHOW_SUCCESS never fires
      // client-side — and the islands sat on 'loading' with no permissions.
      if (store.getState().user?.currentUser) {
        syncFromRedux(store);
      }
    }

    return (next: (action: ReduxAction) => unknown) => (action: ReduxAction) => {
      const result = next(action);

      if (typeof window === 'undefined') {
        return result;
      }

      if (action?.type === CURRENT_USER_SHOW_SUCCESS) {
        syncFromRedux(store);
      } else if (action?.type === FETCH_CURRENT_USER_MEMBERSHIP_SUCCESS) {
        // user.duck.js fetches membership before it dispatches SHOW_SUCCESS, so
        // the first one lands while Redux has no user yet; SHOW_SUCCESS maps
        // both. The one to catch is hooks/useCurrentUserMembership's retry of
        // a failed lookup, which arrives after the user was mapped without it.
        if (store.getState().user?.currentUser) {
          syncFromRedux(store);
        }
      } else if (action?.type === CURRENT_USER_SHOW_ERROR) {
        useAuthentication.setState((state) => ({ status: statusFor(state.user) }));
      } else if (action?.type === CLEAR_CURRENT_USER) {
        // Fired by the legacy logout thunk (dispatch(clearCurrentUser())) —
        // logout itself never dispatches CURRENT_USER_SHOW_SUCCESS/ERROR, so
        // without this branch the v2 store would keep showing the stale
        // logged-in user after a real, successful logout.
        useAuthentication.setState({ user: null, status: 'anonymous' });
      }

      return result;
    };
  };
